import { useFrameworkTemplateCategories } from "./FrameworkTemplateCategories.hooks.ts";
import { DotLegalButton, DotLegalDeleteDialog, DotLegalPageHeader } from "@dotlegal/dotlegal-ui-components";
import React from "react";
import FrameworkCategoryTable from "../../../framework/frameworkCategory/FrameworkCategoryTable.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import FrameworkTemplateCategoryDialog from "./frameworkTemplateCategoryDialog/FrameworkTemplateCategoryDialog.tsx";
import { FrameworkCategoryViewModel } from "../../../framework/frameworkCategory/FrameworkCategories.types.ts";
import { FrameworkTemplateCategoryViewModel } from "./FrameworkTemplateCategories.types.ts";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";

export interface FrameworkTemplateCategoryProps {}

function FrameworkTemplateCategories() {
    let userContext = useUserContext();
    const urlProvider = useUrlProvider();
    const { translateString } = useTranslation();
    const {
        id,
        isLoading,
        data,
        showDialog,
        setShowDialog,
        refetch,
        selectedCategory,
        setSelectedCategory,
        showDeleteCategory,
        setShowDeleteCategory,
        onDeleteClick,
        onSelectCategory,
    } = useFrameworkTemplateCategories();

    function convertToTableModel(category: FrameworkTemplateCategoryViewModel): FrameworkCategoryViewModel {
        const language = category.languages.find((l) => l.twoLetterIsoLanguage === userContext.language);
        return {
            id: category.id,
            customId: category.customId,
            name: language?.name ?? "",
            description: language?.description ?? "",
        };
    }

    return (
        <>
            <DotLegalPageHeader
                breadCrumbs={[
                    { name: "Settings", link: urlProvider.getSettingsUrl() },
                    { name: "Framework templates", link: urlProvider.getFrameworkTemplatesUrl() },
                    { name: data?.frameworkName ?? "", link: urlProvider.getFrameworkTemplateOverviewUrl(id!) },
                    { name: "Framework template categories" },
                ]}
                rightComponent={
                    <DotLegalButton buttonType="primary" size="medium" onClick={() => setShowDialog(true)}>
                        {`${translateString("create")} ${translateString("category").toLowerCase()}`}
                    </DotLegalButton>
                }
            />

            <FrameworkCategoryTable
                isLoading={isLoading}
                data={data?.categories.map((x) => convertToTableModel(x))}
                onUpdateCategoryClick={(id) => {
                    onSelectCategory(id);
                    setShowDialog(true);
                }}
                onDeleteCategoryClick={(id) => {
                    onSelectCategory(id);
                    setShowDeleteCategory(true);
                }}
            />

            {showDialog && (
                <FrameworkTemplateCategoryDialog
                    frameworkTemplateId={id!}
                    model={selectedCategory}
                    onDialogClose={() => {
                        setShowDialog(false);
                        setSelectedCategory(undefined);
                        refetch();
                    }}
                />
            )}

            {showDeleteCategory && selectedCategory && (
                <DotLegalDeleteDialog
                    open
                    headerText={`Delete category: ${selectedCategory.languages[0].name}`}
                    alertBodyText={`Are you sure you want to delete '${selectedCategory.languages[0].name}'?'`}
                    warningTranslation={""}
                    deleteBtnText={"Delete"}
                    cancelBtnText={"Cancel"}
                    onDialogClose={() => {
                        setShowDeleteCategory(false);
                        setSelectedCategory(undefined);
                    }}
                    onOkClick={onDeleteClick}
                />
            )}
        </>
    );
}

export default FrameworkTemplateCategories;
